import { useState, useEffect, useCallback, FC } from 'react';
import {
  Copy,
  CheckCircle2,
  Clock,
  Layers,
  Image as ImageIcon,
  Zap,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { Drawer } from '../ui/Drawer';
import { PageHeader } from '../widgets/PageHeader';
import { MetricWidget } from '../widgets/MetricWidget';
import { DataTable, Column } from '../widgets/DataTable';
import { LoadingSkeleton } from '../ui/LoadingSkeleton';
import {
  fetchMirrorConfig,
  saveMirrorConfig,
  fetchMirrorLogs,
  simulateMirrorMessage,
  MirrorConfig,
  MirrorEventLog,
} from '../../api/mirror';
import {
  fetchGroups,
  fetchAvailableGroups,
  WhatsAppGroup,
  AvailableWhatsAppGroup,
} from '../../api/groups';

export interface MirrorGroupViewProps {
  onNavigateToGroups?: () => void;
}

export const MirrorGroupView: FC<MirrorGroupViewProps> = ({
  onNavigateToGroups,
}) => {
  const { token } = useAuth();
  const { success, error: showError, info } = useToast();

  const [_config, setConfig] = useState<MirrorConfig | null>(null);
  const [logs, setLogs] = useState<MirrorEventLog[]>([]);
  const [localGroups, setLocalGroups] = useState<WhatsAppGroup[]>([]);
  const [availableGroups, setAvailableGroups] = useState<
    AvailableWhatsAppGroup[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Formulário de Configuração
  const [sourceJid, setSourceJid] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [isManualJid, setIsManualJid] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [replaceImage, setReplaceImage] = useState(true);
  const [autoDispatch, setAutoDispatch] = useState(true);
  const [targetGroupIds, setTargetGroupIds] = useState<string[]>([]);

  // Drawer de Simulação
  const [isSimulateOpen, setIsSimulateOpen] = useState(false);
  const [simText, setSimText] = useState(
    'Super oferta do dia! Echo Dot 5ª Geração com Alexa por R$ 289,00 https://amzn.to/exemplo321 Aproveitem antes que acabe!'
  );
  const [simImageUrl, setSimImageUrl] = useState(
    'https://images.unsplash.com/photo-1543512214-318c7553f230?w=500'
  );
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState<Record<
    string,
    any
  > | null>(null);

  const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());

  const loadData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const [cfg, logsData, groupsData] = await Promise.all([
        fetchMirrorConfig(token).catch(() => null),
        fetchMirrorLogs(token, 50).catch(() => []),
        fetchGroups(token).catch(() => []),
      ]);

      if (cfg) {
        setConfig(cfg);
        setSourceJid(cfg.source_group_jid || '');
        setSourceName(cfg.source_group_name || '');
        setIsActive(cfg.is_active);
        setReplaceImage(cfg.replace_image);
        setAutoDispatch(cfg.auto_dispatch);
        setTargetGroupIds(cfg.target_group_ids || []);
      }
      setLogs(logsData);
      setLocalGroups(groupsData);
      setLastRefreshedAt(new Date());
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Falha ao carregar dados de espelhamento.';
      showError(msg);
    } finally {
      setIsLoading(false);
    }

    // Carrega grupos do chip de forma assíncrona desacoplada sem travar a interface
    setIsLoadingAvailable(true);
    try {
      const availData = await fetchAvailableGroups(token).catch(() => []);
      setAvailableGroups(availData);
    } finally {
      setIsLoadingAvailable(false);
    }
  }, [token, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Polling em tempo real a cada 4 segundos para monitoramento ao vivo dos logs de espelhamento
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(async () => {
      try {
        const freshLogs = await fetchMirrorLogs(token, 50);
        setLogs(freshLogs);
        setLastRefreshedAt(new Date());
      } catch {
        // Silencioso para não interromper a navegação em caso de oscilações
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [token]);

  const handleSaveConfig = async () => {
    if (!token) return;
    if (!sourceJid.trim()) {
      showError(
        'Informe o JID ou selecione o grupo de origem.',
        'Campo Obrigatório'
      );
      return;
    }

    setIsSaving(true);
    try {
      const updated = await saveMirrorConfig(token, {
        source_group_jid: sourceJid.trim(),
        source_group_name: sourceName.trim() || undefined,
        is_active: isActive,
        replace_image: replaceImage,
        auto_dispatch: autoDispatch,
        target_group_ids: targetGroupIds,
      });
      setConfig(updated);
      success(
        'Configurações de espelhamento salvas com sucesso.',
        'Espelho Atualizado'
      );
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao salvar configurações.';
      showError(msg, 'Erro ao Salvar');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTargetGroup = (groupId: string) => {
    setTargetGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSimulate = async () => {
    if (!token) return;
    if (!simText.trim()) {
      showError(
        'Insira o texto da mensagem para simulação.',
        'Campo Obrigatório'
      );
      return;
    }

    setIsSimulating(true);
    setSimulationResult(null);
    try {
      const res = await simulateMirrorMessage(token, {
        text: simText.trim(),
        image_url: simImageUrl.trim() || undefined,
        source_group_jid: sourceJid.trim() || undefined,
      });
      setSimulationResult(res);
      success('Simulação concluída com sucesso.', 'Teste Finalizado');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao simular espelhamento.';
      showError(msg, 'Erro na Simulação');
    } finally {
      setIsSimulating(false);
    }
  };

  // Detecção de alterações pendentes de salvamento
  const isDirty = Boolean(
    _config &&
    (sourceJid !== (_config.source_group_jid || '') ||
      sourceName !== (_config.source_group_name || '') ||
      isActive !== _config.is_active ||
      replaceImage !== _config.replace_image ||
      autoDispatch !== _config.auto_dispatch ||
      JSON.stringify(targetGroupIds.slice().sort()) !==
        JSON.stringify((_config.target_group_ids || []).slice().sort()))
  );

  // Métricas calculadas
  const clonedCount = logs.filter((l) => l.status === 'cloned').length;
  const ignoredCount = logs.filter((l) => l.status === 'ignored').length;
  const totalProcessed = logs.length;
  const successRate =
    totalProcessed > 0 ? Math.round((clonedCount / totalProcessed) * 100) : 100;

  const columns: Column<MirrorEventLog>[] = [
    {
      key: 'created_at',
      header: 'Data / Hora',
      render: (log) => {
        try {
          const d = new Date(log.created_at);
          return (
            <div className="flex flex-col text-micro text-content-muted">
              <span className="font-medium text-content-main">
                {d.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
              <span>
                {d.toLocaleDateString([], { day: '2-digit', month: '2-digit' })}
              </span>
            </div>
          );
        } catch {
          return (
            <span className="text-micro text-content-muted">
              {log.created_at}
            </span>
          );
        }
      },
    },
    {
      key: 'marketplace',
      header: 'Marketplace & ID',
      render: (log) => {
        const mp = log.marketplace || 'other';
        const labelMap: Record<string, string> = {
          amazon: 'Amazon',
          mercadolivre: 'Mercado Livre',
          shopee: 'Shopee',
          magalu: 'Magalu',
          other: 'Outro',
        };
        return (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-content-main">
              {labelMap[mp] || mp}
            </span>
            {log.product_identifier && (
              <span className="font-mono text-2xs text-brand-600 dark:text-brand-400">
                {log.product_identifier}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'image_source',
      header: 'Foto do Produto',
      render: (log) => {
        if (log.image_source === 'catalog') {
          return (
            <div className="flex items-center gap-2">
              {log.final_image_url && (
                <img
                  src={log.final_image_url}
                  alt="Catálogo"
                  className="h-8 w-8 rounded-md border border-surface-border object-cover"
                />
              )}
              <Badge variant="online">Catálogo Oficial</Badge>
            </div>
          );
        }
        if (log.image_source === 'fallback_original') {
          return (
            <div className="flex items-center gap-2">
              {log.final_image_url && (
                <img
                  src={log.final_image_url}
                  alt="Original"
                  className="h-8 w-8 rounded-md border border-surface-border object-cover"
                />
              )}
              <Badge variant="warning">Foto Original</Badge>
            </div>
          );
        }
        return <span className="text-micro text-content-muted">Sem foto</span>;
      },
    },
    {
      key: 'affiliate_url',
      header: 'Link Afiliado Injetado',
      render: (log) => {
        if (!log.affiliate_url) {
          return (
            <span className="text-micro text-content-muted">
              {log.reason || '—'}
            </span>
          );
        }
        return (
          <div className="flex max-w-xs items-center gap-1.5">
            <a
              href={log.affiliate_url}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-micro font-medium text-brand-600 hover:underline dark:text-brand-400"
              title={log.affiliate_url}
            >
              {log.affiliate_url}
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(log.affiliate_url || '');
                info('Link copiado para a área de transferência.');
              }}
              className="rounded p-1 text-content-muted hover:bg-surface-subtle hover:text-content-main"
              title="Copiar link"
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status & Entrega',
      render: (log) => {
        if (log.status === 'cloned') {
          return (
            <div className="flex flex-col items-start gap-1">
              <Badge variant="online">Clonado & Enviado</Badge>
              <span className="text-micro font-medium text-emerald-600 dark:text-emerald-400">
                Postado no Grupo VIP
              </span>
            </div>
          );
        }
        if (log.status === 'ignored') {
          return (
            <div className="flex flex-col items-start gap-1">
              <Badge variant="neutral">Filtrado</Badge>
              <span
                className="text-micro text-content-muted"
                title={log.reason || 'Mensagem sem link'}
              >
                {log.reason || 'Sem produto/link'}
              </span>
            </div>
          );
        }
        return (
          <div className="flex flex-col items-start gap-1">
            <Badge variant="offline">Falha</Badge>
            <span
              className="text-micro text-rose-500"
              title={log.reason || 'Falha no processamento'}
            >
              {log.reason || 'Erro no envio'}
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Subnavegação e Cabeçalho */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          title="Espelho Automático & Troca de Foto"
          subtitle="Espelhamento de Grupo: monitore canais ou grupos internos em tempo real, substitua links pelo seu ID de afiliado e publique com foto oficial de catálogo."
        />
        <div className="flex items-center gap-2">
          {onNavigateToGroups && (
            <Button variant="ghost" size="sm" onClick={onNavigateToGroups}>
              Ver Grupos VIP
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsSimulateOpen(true)}
          >
            Simular Mensagem
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSaveConfig}
            isLoading={isSaving}
          >
            Salvar Configurações
          </Button>
        </div>
      </div>

      {/* Widgets de Telemetria */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricWidget
          title="Ofertas Espelhadas"
          value={isLoading ? '...' : clonedCount}
          description="Mensagens com produto convertidas e despachadas"
          icon={<Zap className="h-5 w-5 text-brand-500" />}
        />
        <MetricWidget
          title="Conversas Filtradas"
          value={isLoading ? '...' : ignoredCount}
          description="Bate-papos e textos sem produto ignorados"
          icon={<Clock className="h-5 w-5 text-content-muted" />}
        />
        <MetricWidget
          title="Taxa de Precisão"
          value={isLoading ? '...' : `${successRate}%`}
          description="Aproveitamento de links com SubID canônico"
          icon={<CheckCircle2 className="h-5 w-5 text-emerald-500" />}
        />
        <MetricWidget
          title="Fila de Entrega"
          value="queue:mirror"
          description="Prioridade Alta (10) com auto-dispatch"
          icon={<Layers className="h-5 w-5 text-brand-500" />}
        />
      </div>

      {/* Painel de Configuração Principal */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Coluna 1 e 2: Origem e Regras */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-content-main">
                    Grupo de Origem no WhatsApp
                  </h3>
                  <p className="text-xs text-content-muted">
                    Selecione o grupo que o chip monitora para clonar ofertas.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-content-muted">
                    Status do Espelho:
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsActive(!isActive)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      isActive ? 'bg-brand-500' : 'bg-surface-subtle'
                    }`}
                  >
                    <span
                      className={`shadow-xs pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white ring-0 transition duration-200 ease-in-out ${
                        isActive ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <Badge variant={isActive ? 'online' : 'offline'}>
                    {isActive ? 'Ativo' : 'Pausado'}
                  </Badge>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="space-y-4">
              {/* Seleção Compacta via Dropdown / Select (estilo criação de grupos) */}
              {!isManualJid &&
              (availableGroups.length > 0 || isLoadingAvailable) ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-content-main">
                      Selecione o Grupo Fonte (detectados no seu WhatsApp
                      conectado)
                    </label>
                    {isLoadingAvailable && (
                      <span className="flex animate-pulse items-center gap-1.5 text-2xs text-brand-600 dark:text-brand-400">
                        <RefreshCw className="h-3 w-3 animate-spin" />{' '}
                        Carregando grupos do chip...
                      </span>
                    )}
                  </div>
                  <select
                    value={sourceJid}
                    onChange={(e) => {
                      const selectedJid = e.target.value;
                      setSourceJid(selectedJid);
                      const found = availableGroups.find(
                        (g) => g.jid === selectedJid
                      );
                      if (found) setSourceName(found.name);
                    }}
                    disabled={
                      isLoadingAvailable && availableGroups.length === 0
                    }
                    className="w-full rounded-lg border border-surface-border bg-surface-panel px-3 py-2.5 text-xs text-content-main focus:border-brand-500 focus:outline-none"
                  >
                    <option value="">
                      {isLoadingAvailable && availableGroups.length === 0
                        ? '-- Carregando grupos do WhatsApp... --'
                        : '-- Escolha o grupo que deseja espelhar --'}
                    </option>
                    {/* Se o grupo salvo no banco ainda não está na lista ou durante carregamento, exibe como opção ativa */}
                    {sourceJid &&
                      !availableGroups.some((ag) => ag.jid === sourceJid) && (
                        <option value={sourceJid}>
                          {sourceName || 'Grupo de Origem Configurado'} (
                          {sourceJid})
                        </option>
                      )}
                    {availableGroups.map((ag) => (
                      <option key={ag.jid} value={ag.jid}>
                        {ag.name} ({ag.current_members} membros)
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center justify-between pt-1 text-2xs text-content-muted">
                    <span>
                      O chip só precisa ser participante (não precisa ser
                      administrador).
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsManualJid(true)}
                      className="cursor-pointer text-brand-600 hover:underline dark:text-brand-400"
                    >
                      Inserir JID manualmente
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-content-main">
                      Identificação do Grupo de Origem
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsManualJid(false)}
                      className="cursor-pointer text-2xs text-brand-600 hover:underline dark:text-brand-400"
                    >
                      Voltar para lista do chip
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Input
                      label="JID do Grupo de Origem"
                      placeholder="ex: 12036302839281@g.us"
                      value={sourceJid}
                      onChange={(e) => setSourceJid(e.target.value)}
                      helperText="ID interno do WhatsApp (terminado em @g.us)"
                    />
                    <Input
                      label="Nome Amigável do Grupo"
                      placeholder="ex: Canal Concorrente Promoções"
                      value={sourceName}
                      onChange={(e) => setSourceName(e.target.value)}
                      helperText="Identificação visual no painel"
                    />
                  </div>
                </div>
              )}

              {/* Informação do grupo ativo selecionado */}
              {sourceJid && (
                <div className="flex items-center justify-between rounded-lg border border-brand-500/20 bg-brand-500/5 p-3 text-xs">
                  <div>
                    <span className="font-semibold text-content-main">
                      {sourceName || 'Grupo de Origem Selecionado'}
                    </span>
                    <span className="block font-mono text-2xs text-content-muted">
                      {sourceJid}
                    </span>
                  </div>
                  <Badge variant="online">Monitorando</Badge>
                </div>
              )}

              {/* Switches de Regras */}
              <div className="space-y-3 border-t border-surface-border pt-3">
                <div className="flex items-center justify-between rounded-xl bg-surface-subtle p-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg border border-surface-border bg-surface-panel p-2 text-brand-500">
                      <ImageIcon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-content-main">
                        Troca Inteligente por Imagem Oficial de Catálogo
                      </div>
                      <div className="text-2xs text-content-muted">
                        Busca a foto oficial em alta resolução no catálogo do
                        marketplace. Se não encontrar, utiliza a foto original
                        anexada como fallback seguro.
                      </div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={replaceImage}
                    onChange={(e) => setReplaceImage(e.target.checked)}
                    className="h-4 w-4 rounded border-surface-border text-brand-600 focus:ring-brand-500"
                  />
                </div>

                <div className="flex items-center justify-between rounded-xl bg-surface-subtle p-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg border border-surface-border bg-surface-panel p-2 text-emerald-500">
                      <Zap className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-content-main">
                        Auto-Dispatch Imediato (Sem Aprovação Manual)
                      </div>
                      <div className="text-2xs text-content-muted">
                        Dispara a oferta diretamente para os canais VIP
                        configurados na fila isolada de prioridade{' '}
                        <code>queue:mirror</code>.
                      </div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={autoDispatch}
                    onChange={(e) => setAutoDispatch(e.target.checked)}
                    className="h-4 w-4 rounded border-surface-border text-brand-600 focus:ring-brand-500"
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Coluna 3: Seleção de Grupos de Destino */}
        <div className="space-y-6">
          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-content-main">
                    Grupos VIP de Destino
                  </h3>
                  <p className="text-xs text-content-muted">
                    {targetGroupIds.length} grupo(s) selecionado(s)
                  </p>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="max-h-[380px] space-y-2 overflow-y-auto">
              {localGroups.length === 0 ? (
                <div className="py-6 text-center text-xs text-content-muted">
                  Nenhum grupo VIP cadastrado. Cadastre grupos na aba Grupos.
                </div>
              ) : (
                localGroups.map((g) => {
                  const isChecked = targetGroupIds.includes(g.id);
                  return (
                    <label
                      key={g.id}
                      onClick={() => toggleTargetGroup(g.id)}
                      className={`flex cursor-pointer items-center justify-between rounded-xl border p-3 transition-colors ${
                        isChecked
                          ? 'border-brand-500 bg-brand-500/5'
                          : 'border-surface-border bg-surface-panel hover:bg-surface-subtle'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                          className="h-4 w-4 rounded border-surface-border text-brand-600 focus:ring-brand-500"
                        />
                        <div>
                          <div className="text-xs font-semibold text-content-main">
                            {g.name}
                          </div>
                          <div className="text-2xs text-content-muted">
                            {g.current_members} membros • {g.status}
                          </div>
                        </div>
                      </div>
                      <Badge variant={g.can_send ? 'online' : 'warning'}>
                        {g.can_send ? 'Pronto' : 'Apenas Adm'}
                      </Badge>
                    </label>
                  );
                })
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Barra de Ação & Status de Salvamento */}
      <div className="flex flex-col items-center justify-between gap-3 rounded-xl border border-surface-border bg-surface-subtle p-4 sm:flex-row">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-content-main">
              Configurações do Espelho
            </span>
            <Badge variant={isDirty ? 'warning' : 'online'}>
              {isDirty ? 'Alterações não salvas' : 'Salvo e Sincronizado'}
            </Badge>
          </div>
          <p className="text-2xs text-content-muted">
            {isDirty
              ? 'Você selecionou ou alterou opções. Clique em "Salvar Configurações" para ativar o espelhamento nos seus grupos.'
              : 'O espelhamento está salvo e monitorando novas mensagens no WhatsApp.'}
          </p>
        </div>
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsSimulateOpen(true)}
          >
            Simular Mensagem
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSaveConfig}
            isLoading={isSaving}
          >
            Salvar Configurações
          </Button>
        </div>
      </div>

      {/* Histórico de Clonagens */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-content-main">
                  Histórico de Clonagens & Telemetria
                </h3>
                <div className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-2xs font-medium text-emerald-600 dark:text-emerald-400">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                  </span>
                  <span>Tempo Real</span>
                </div>
              </div>
              <p className="text-xs text-content-muted">
                Registro detalhado de mensagens capturadas, identificadores
                extraídos e links afiliados gerados (sincronização a cada 4s).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden text-2xs text-content-muted sm:inline">
                Última checagem: {lastRefreshedAt.toLocaleTimeString('pt-BR')}
              </span>
              <Button variant="ghost" size="sm" onClick={loadData}>
                Atualizar
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {logs.length > 0 && (
            <div className="mb-4 flex flex-col justify-between gap-1 rounded-lg border border-brand-500/20 bg-brand-500/5 p-3 text-2xs sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-content-main">
                  Último evento interceptado:
                </span>
                <span className="text-content-muted">
                  {new Date(logs[0].created_at).toLocaleTimeString('pt-BR')} —{' '}
                  {logs[0].status === 'cloned' ? (
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">
                      Clonado & Despachado para o Grupo VIP
                    </span>
                  ) : (
                    <span className="font-medium text-amber-600 dark:text-amber-400">
                      Filtrado ({logs[0].reason || 'sem link'})
                    </span>
                  )}
                </span>
              </div>
              <span className="text-content-muted">
                {logs.filter((l) => l.status === 'cloned').length} oferta(s)
                despachada(s)
              </span>
            </div>
          )}
          {isLoading ? (
            <div className="space-y-3">
              <LoadingSkeleton className="h-10 w-full" />
              <LoadingSkeleton className="h-10 w-full" />
              <LoadingSkeleton className="h-10 w-full" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center text-xs text-content-muted">
              Nenhuma mensagem capturada até o momento. Configure o grupo de
              origem ou use o botão Simular Mensagem.
            </div>
          ) : (
            <DataTable
              data={logs}
              columns={columns}
              keyExtractor={(l) => l.id}
            />
          )}
        </Card.Body>
      </Card>

      {/* Drawer de Simulação de Mensagem */}
      <Drawer
        isOpen={isSimulateOpen}
        onClose={() => setIsSimulateOpen(false)}
        title="Simular Mensagem do Espelho"
      >
        <div className="space-y-4">
          <p className="text-xs text-content-muted">
            Cole uma mensagem promocional concorrente ou apenas um link para
            testar a higienização de marcas de terceiros, a injeção do seu link
            de afiliado e a busca de foto oficial.
          </p>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-content-main">
              Texto da Mensagem ou Link
            </label>
            <textarea
              rows={4}
              value={simText}
              onChange={(e) => setSimText(e.target.value)}
              className="w-full rounded-xl border border-surface-border bg-surface-panel p-3 text-xs text-content-main focus:border-brand-500 focus:outline-none"
              placeholder="Cole a mensagem promocional ou link aqui..."
            />
          </div>

          <Input
            label="URL da Foto Anexada (Opcional)"
            placeholder="https://..."
            value={simImageUrl}
            onChange={(e) => setSimImageUrl(e.target.value)}
          />

          <Button
            variant="primary"
            size="md"
            onClick={handleSimulate}
            isLoading={isSimulating}
            className="w-full"
          >
            Executar Simulação do Pipeline
          </Button>

          {/* Resultado da Simulação */}
          {simulationResult && (
            <div className="space-y-3 rounded-xl border border-surface-border bg-surface-subtle p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-content-main">
                  Resultado do Teste
                </span>
                <Badge
                  variant={
                    simulationResult.status === 'cloned' ? 'online' : 'neutral'
                  }
                >
                  {simulationResult.status === 'cloned'
                    ? 'Oferta Processada'
                    : 'Mensagem Ignorada'}
                </Badge>
              </div>

              {simulationResult.reason && (
                <p className="text-2xs text-content-muted">
                  {String(simulationResult.reason)}
                </p>
              )}

              {/* Se foi clonado, mostra a prévia da mensagem e foto como no WhatsApp */}
              {simulationResult.status === 'cloned' && (
                <div className="space-y-3 border-t border-surface-border pt-2">
                  <span className="text-2xs font-semibold uppercase tracking-wider text-content-muted">
                    Prévia da Mensagem no WhatsApp
                  </span>

                  <div className="space-y-2 rounded-xl border border-surface-border bg-surface-panel p-3">
                    {Boolean(simulationResult.final_image_url) && (
                      <div className="overflow-hidden rounded-lg border border-surface-border bg-black/5 dark:bg-white/5">
                        <img
                          src={String(simulationResult.final_image_url)}
                          alt="Prévia do Produto"
                          className="h-44 w-full object-contain"
                        />
                      </div>
                    )}

                    <div className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-content-main">
                      {String(
                        simulationResult.modified_text ||
                          simulationResult.original_text ||
                          ''
                      )}
                    </div>
                  </div>

                  {/* Metadados técnicos */}
                  <div className="grid grid-cols-2 gap-2 font-mono text-2xs text-content-muted">
                    {Boolean(simulationResult.marketplace) && (
                      <div className="rounded-lg border border-surface-border bg-surface-panel p-2">
                        <span className="block text-content-muted">
                          Marketplace:
                        </span>
                        <b className="font-semibold text-content-main">
                          {String(simulationResult.marketplace).toUpperCase()}
                        </b>
                      </div>
                    )}
                    {Boolean(simulationResult.image_source) && (
                      <div className="rounded-lg border border-surface-border bg-surface-panel p-2">
                        <span className="block text-content-muted">
                          Fonte da Foto:
                        </span>
                        <b className="font-semibold text-content-main">
                          {simulationResult.image_source === 'catalog'
                            ? 'Catálogo Oficial'
                            : 'Foto Original'}
                        </b>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};
